import React from 'react';
import { RatingBadge } from './RatingBadge';
import { CTAButton } from './CTAButton';
import { CheckCircle2, XCircle } from 'lucide-react';
interface ProductCardProps {
  rank: number;
  name: string;
  rating: number;
  grade: string;
  badge?: string;
  imageUrl: string;
  pros: string[];
  cons: string[];
  conclusion: string;
  testimonials?: string[];
}
export function ProductCard({
  rank,
  name,
  rating,
  grade,
  badge,
  imageUrl,
  pros,
  cons,
  conclusion,
  testimonials
}: ProductCardProps) {
  const botanikUrl = 'https://trybotanik.com/products/botanik-moringa?tw_step=offer&_gl=1*1eawun*_gcl_au*NTI4MjE1NDg5LjE3NjEzNzQ5Mzk.*_ga*NjYxNzkyMTkxLjE3NjEzNzQ5Mzk.*_ga_KDTN4K6RTM*czE3NjM1MDYxNDUkbzQkZzEkdDE3NjM1MDYxNDgkajU3JGwwJGgw';
  const organicIndiaUrl = 'https://amzn.to/4adr8Gp';
  const rosabellaUrl = 'https://amzn.to/48h1eis';
  const kuliKuliUrl = 'https://amzn.to/44vUbBp';
  const puraVidaUrl = 'https://amzn.to/4igAGm1';
  // Determine which URL to use based on rank
  const getCtaUrl = () => {
    if (rank === 1) return botanikUrl;
    if (rank === 2) return organicIndiaUrl;
    if (rank === 3) return rosabellaUrl;
    if (rank === 4) return kuliKuliUrl;
    if (rank === 5) return puraVidaUrl;
    return undefined;
  };
  return <div className="bg-white border-2 border-gray-300 rounded-lg p-6 sm:p-8 mb-10 shadow-md" data-id="element-27">
      {/* Header Section - Responsive Layout */}
      <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8 mb-8" data-id="element-28">
        {/* Product Image */}
        <div className="flex-shrink-0 mx-auto sm:mx-0" data-id="element-29">
          <img src={imageUrl} alt={name} className="w-32 h-32 sm:w-40 sm:h-40 object-contain" data-id="element-30" />
        </div>

        {/* Product Info */}
        <div className="flex-1 w-full" data-id="element-31">
          <div className="flex flex-col gap-3 mb-4" data-id="element-32">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight" data-id="element-33">
              #{rank}. {name}
            </h3>
            {badge && <span className="bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full text-base sm:text-lg font-bold inline-block w-fit" data-id="element-34">
                {badge}
              </span>}
          </div>
          <div className="flex items-center gap-4 text-base sm:text-lg text-gray-700 font-medium" data-id="element-35">
            <span className="font-bold" data-id="element-36">{rating}/10</span>
            <span data-id="element-37">|</span>
            <span data-id="element-38">
              Grade: <span className="font-bold" data-id="element-39">{grade}</span>
            </span>
          </div>
        </div>

        {/* Rating Badge - Hidden on mobile, shown on tablet+ */}
        <div className="hidden sm:block flex-shrink-0" data-id="element-40">
          <RatingBadge score={rating} size="large" data-id="element-41" />
        </div>
      </div>

      {/* CTA Button for Rank #1 Only */}
      {rank === 1 && <div className="text-center mb-8" data-id="element-42">
          <CTAButton href={botanikUrl} data-id="element-43">
            ➤ Click To Learn More About Botanik
          </CTAButton>
        </div>}

      {/* Pros and Cons - Stack on mobile, side-by-side on tablet+ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-44">
        <div className="bg-green-50 rounded-lg p-6 border-2 border-green-200" data-id="element-45">
          <h4 className="font-bold text-green-700 mb-5 text-xl sm:text-2xl" data-id="element-46">
            PROS
          </h4>
          <ul className="space-y-4" data-id="element-47">
            {pros.map((pro, index) => <li key={index} className="flex items-start gap-3 sm:gap-4" data-id="element-48">
                <CheckCircle2 className="w-6 h-6 sm:w-7 sm:h-7 text-green-600 flex-shrink-0 mt-0.5" data-id="element-49" />
                <span className="text-base sm:text-lg leading-relaxed text-gray-800" data-id="element-50">
                  {pro}
                </span>
              </li>)}
          </ul>
        </div>
        <div className="bg-red-50 rounded-lg p-6 border-2 border-red-200" data-id="element-51">
          <h4 className="font-bold text-red-700 mb-5 text-xl sm:text-2xl" data-id="element-52">
            CONS
          </h4>
          <ul className="space-y-4" data-id="element-53">
            {cons.map((con, index) => <li key={index} className="flex items-start gap-3 sm:gap-4" data-id="element-54">
                <XCircle className="w-6 h-6 sm:w-7 sm:h-7 text-red-600 flex-shrink-0 mt-0.5" data-id="element-55" />
                <span className="text-base sm:text-lg leading-relaxed text-gray-800" data-id="element-56">
                  {con}
                </span>
              </li>)}
          </ul>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mb-8 bg-gray-50 rounded-lg p-6 border-2 border-gray-200" data-id="element-57">
        <h4 className="font-bold text-gray-900 mb-4 text-xl sm:text-2xl" data-id="element-58">
          Conclusion
        </h4>
        <p className="text-base sm:text-lg text-gray-800 leading-relaxed" data-id="element-59">
          {conclusion}
        </p>
      </div>

      {/* Testimonials */}
      {testimonials && testimonials.length > 0 && <div className="mb-8 bg-blue-50 p-6 rounded-lg border-2 border-blue-200" data-id="element-60">
          <h4 className="font-bold text-gray-900 mb-4 text-xl sm:text-2xl" data-id="element-61">
            What Customers Say:
          </h4>
          {testimonials.map((testimonial, index) => <p key={index} className="text-base sm:text-lg text-gray-800 italic mb-4 last:mb-0 leading-relaxed" data-id="element-62">
              "{testimonial}"
            </p>)}
        </div>}

      {/* CTA Button */}
      <div className="text-center mt-8" data-id="element-63">
        <CTAButton href={getCtaUrl()} data-id="element-64">➤ Click To Learn More</CTAButton>
      </div>
    </div>;
}